import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useSpeakersTest } from 'hooks/useSpeakersTest';
import { useDeviceTest } from 'hooks/useDeviceTest';
import { useCountdownTimer } from 'use-countdown-timer';
import { formatErrorForLogging, logger } from 'utils/logger';

const TIMEOUT = 1000 * 5;

export const useAudioTest = () => {
  const [testStatus, setTestStatus] = useDeviceTest();
  const { playTestSound, stopTestSound } = useSpeakersTest();
  const {
    countdown,
    start: startCountdown,
    reset: resetCountdown,
  } = useCountdownTimer({
    timer: TIMEOUT,
  });

  const chunksRef = useRef<BlobPart[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const releaseRecording = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stopTest = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    chunksRef.current = [];
    releaseRecording();

    stopTestSound();
    resetCountdown();
    setTestStatus('idle');
  }, [releaseRecording, stopTestSound, resetCountdown, setTestStatus]);

  const handleRecordingStart = useCallback(() => {
    setTestStatus('processing');
    startCountdown();
  }, [setTestStatus, startCountdown]);

  const handleRecordingError = useCallback(() => {
    setTestStatus('failure');
  }, [setTestStatus]);

  const handleRecordingStop = useCallback(async () => {
    const blob = new Blob(chunksRef.current, { type: 'audio/mpeg' });
    chunksRef.current = [];

    releaseRecording();
    const audioURL = URL.createObjectURL(blob);
    objectUrlRef.current = audioURL;

    setTestStatus('success');
    const outcome = await playTestSound({
      url: audioURL,
      onEnd: () => {
        releaseRecording();
        setTestStatus('idle');
      },
      onError: () => {
        releaseRecording();
        setTestStatus('failure');
      },
    });

    // 'aborted' means a deliberate stop already reset the state; only a real
    // failure (including the guard-blocked path, which skips onError) lands here.
    if (outcome === 'failed') {
      releaseRecording();
      setTestStatus('failure');
    }
  }, [setTestStatus, playTestSound, releaseRecording]);

  const handleDataAvailable = useCallback((e: BlobEvent) => {
    chunksRef.current.push(e.data);
  }, []);

  const testAudio = useCallback(
    async (stream: MediaStream) => {
      // 'failure' must stay re-enterable or the button wedges for the
      // component's lifetime.
      if (testStatus !== 'idle' && testStatus !== 'failure') {
        return;
      }

      // A retry from 'failure' can still have the previous attempt's timeout
      // pending; left uncleared it would stop the new recorder early.
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      // MediaRecorder construction and start() throw synchronously; callers
      // discard the promise, so an uncaught throw becomes an unhandled rejection.
      try {
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = handleDataAvailable;
        mediaRecorder.onstart = handleRecordingStart;
        mediaRecorder.onerror = handleRecordingError;
        mediaRecorder.onstop = handleRecordingStop;

        mediaRecorder.start();
      } catch (error) {
        setTestStatus('failure');
        logger
          .remote()
          .error('Microphone test recorder failed to start', formatErrorForLogging(error));
        Sentry.captureException(error);
        return;
      }

      timeoutIdRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, TIMEOUT);

      logger.remote({ tier: 1 }).info('User tested microphone');
    },
    [
      testStatus,
      setTestStatus,
      handleDataAvailable,
      handleRecordingStart,
      handleRecordingError,
      handleRecordingStop,
    ]
  );

  return {
    status: testStatus,
    countdown: Math.floor((countdown / 1000) % 60),
    testAudio,
    stopTest,
  };
};
